import { useEffect, useState } from 'react'
import { Screen, SectionHeader } from '@/components/layout'
import { Button } from '@/components/primitives'
import { AmountStepper, CurrencyPicker } from '@/components/pickers'
import { List, SettingRow, ToggleRow } from '@/components/rows'
import { ErrorState, LoadingRegion, SkeletonList } from '@/components/states'
import type { Money } from '@/generated/types'
import { ApiFailure, api, type UserSettings } from '@/lib/api'
import { t } from '@/lib/copy'
import { useNavigate } from '@/lib/router'
import { useSession } from '@/lib/session'
import { useAsync } from '@/lib/useAsync'

/** Account, currency, goals, notifications, and data controls. */
export function SettingsScreen() {
  const navigate = useNavigate()
  const { signOut } = useSession()
  const settings = useAsync(() => api.settings.get(), [])

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<ApiFailure | null>(null)
  const [target, setTarget] = useState<Money | null>(null)

  const data = settings.state.data

  useEffect(() => {
    if (data && target === null) {
      setTarget(data.emergencyFundTarget ?? { amount: 0, currency: data.currency })
    }
  }, [data, target])

  const screenState = saving
    ? 'saving'
    : error
      ? 'error'
      : settings.state.status === 'loading' && !data
        ? 'loading'
        : settings.state.status === 'error'
          ? 'error'
          : 'ready'

  const patch = async (changes: Partial<UserSettings>) => {
    if (!data) return
    const previous = data
    settings.setData({ ...data, ...changes })
    setSaving(true)
    setError(null)
    try {
      const updated = await api.settings.update(changes)
      settings.setData(updated)
    } catch (e) {
      settings.setData(previous)
      setError(e instanceof ApiFailure ? e : null)
    } finally {
      setSaving(false)
    }
  }

  if (screenState === 'loading') {
    return (
      <Screen id="settings" state={screenState}>
        <LoadingRegion>
          <SkeletonList rows={8} />
        </LoadingRegion>
      </Screen>
    )
  }

  if (!data) {
    return (
      <Screen id="settings" state="error">
        <ErrorState
          titleKey="settings.error.title"
          error={settings.state.status === 'error' ? settings.state.error : undefined}
          onRetry={settings.reload}
        />
      </Screen>
    )
  }

  return (
    <Screen id="settings" state={screenState}>
      <div className="stack">
        {error ? <ErrorState titleKey="settings.error.title" error={error} onRetry={() => setError(null)} /> : null}

        <section>
          <SectionHeader title={t('settings.section.account')} />
          <List>
            <SettingRow label={t('settings.row.email')} value={data.email} />
            <SettingRow label={t('settings.row.accounts')} onSelect={() => navigate('accounts')} />
            <SettingRow
              label={t('settings.row.connectedAccounts')}
              onSelect={() => navigate('connectedAccounts')}
            />
            <SettingRow
              label={t('settings.row.signInMethods')}
              onSelect={() => navigate('signInMethods')}
            />
          </List>
        </section>

        <section>
          <SectionHeader title={t('settings.section.preferences')} />
          <div className="card stack">
            <CurrencyPicker
              value={data.currency}
              label={t('settings.row.currency')}
              onChange={(currency) => void patch({ currency })}
            />
            {target ? (
              <>
                <AmountStepper
                  value={target}
                  onChange={setTarget}
                  label={t('settings.row.emergencyFundTarget')}
                  step={500}
                />
                <Button
                  variant="secondary"
                  disabled={saving || target.amount === (data.emergencyFundTarget?.amount ?? 0)}
                  onClick={() => void patch({ emergencyFundTarget: target })}
                >
                  {t('cta.save')}
                </Button>
              </>
            ) : null}
          </div>
        </section>

        <section>
          <SectionHeader title={t('settings.row.notifications')} />
          <List>
            <ToggleRow
              label={t('settings.row.notifications.newAnalysis')}
              checked={data.notifications.newAnalysis}
              onChange={(newAnalysis) =>
                void patch({ notifications: { ...data.notifications, newAnalysis } })
              }
            />
            <ToggleRow
              label={t('settings.row.notifications.priceIncrease')}
              checked={data.notifications.priceIncrease}
              onChange={(priceIncrease) =>
                void patch({ notifications: { ...data.notifications, priceIncrease } })
              }
            />
            <ToggleRow
              label={t('settings.row.notifications.fee')}
              checked={data.notifications.fee}
              onChange={(fee) => void patch({ notifications: { ...data.notifications, fee } })}
            />
          </List>
        </section>

        <section>
          <SectionHeader title={t('settings.section.data')} />
          <List>
            <SettingRow label={t('settings.row.export')} onSelect={() => navigate('export')} />
            <SettingRow label={t('settings.row.privacy')} onSelect={() => navigate('privacy')} />
          </List>
        </section>

        <Button variant="destructive" onClick={() => void signOut()}>
          {t('settings.cta.signOut')}
        </Button>
      </div>
    </Screen>
  )
}
